import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetQuizQuestionQuery,
  useUpdateQuizQuestionMutation,
  useGetProductsQuery
} from './productKnowledgeApiSlice';
import {
  Container,
  Header,
  Form,
  Button,
  Tab,
  Icon,
  Loader,
  Message,
  Select,
  TextArea,
  Label,
  Input
} from 'semantic-ui-react';

// Reusing style from ProductDetails
const innerTabPaneStyle = {
    padding: '32px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.04)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.02)',
    marginTop: '0'
};

const inputStyle = {
    borderRadius: '10px',
    backgroundColor: '#F5F5F7',
    border: 'none',
    fontSize: '14px',
    padding: '12px 14px'
};

const QUESTION_TYPES = [
  { key: 'MC', value: 'MC', text: '객관식 (Multiple Choice)' },
  { key: 'OE', value: 'OE', text: '주관식 (Open Ended)' }
];

const MultipleChoiceEditor = ({ meta, onChange }) => {
    
    // Ensure meta is an object
    const safeMeta = meta || {};
    // Ensure we have 4 options structure
    const options = (safeMeta.options && safeMeta.options.length > 0) 
        ? safeMeta.options 
        : ['A. ', 'B. ', 'C. ', 'D. '];
        
    const explanation = safeMeta.explanation || '';
    const correctAnswer = safeMeta.correct_answer || '';
    
    // Helper to extract option label (e.g. "A." from "A. Description")
    const getOptionLabel = (index) => String.fromCharCode(65 + index); // 0 -> A, 1 -> B 

    const cleanOptionText = (text, index) => {
        if (!text) return '';
        const label = getOptionLabel(index);
        // Robust regex: Matches start of string, "A", optional ".", optional space(s)
        const regex = new RegExp(`^${label}\\.?\\s*`, 'i');
        return text.replace(regex, '');
    };

    const handleOptionChange = (index, content) => {
        const newOptions = [...options];
        const oldFullValue = newOptions[index];
        const label = getOptionLabel(index);
        
        // Always force standard format "A. Content"
        const newFullValue = `${label}. ${content}`;
        
        newOptions[index] = newFullValue;
        
        // Automatic Update of Correct Answer
        let newCorrectAnswer = correctAnswer;
        
        // If the OLD value matches the Current Correct Answer, assume we need to update the Correct Answer too
        if (oldFullValue === correctAnswer) {
            newCorrectAnswer = newFullValue;
        } 
        // Edge case: if correct answer was just the content or empty, we might lose sync, 
        // but this logic assumes consistency.
        
        onChange({ ...safeMeta, options: newOptions, correct_answer: newCorrectAnswer });
    };

    const handleExplanationChange = (e, { value }) => {
        onChange({ ...safeMeta, explanation: value });
    };

    const handleCorrectAnswerSelect = (optionValue) => {
        onChange({ ...safeMeta, correct_answer: optionValue });
    };

    return (
        <div style={{ marginTop: '30px', padding: '24px', backgroundColor: '#F5F5F7', borderRadius: '16px' }}>
            <Header as="h4" style={{ color: '#1d1d1f', marginBottom: '20px' }}>객관식 설정 (Multiple Choice)</Header>
            
            <Form.Field required>
                <label style={{ fontSize: '13px', color: '#6e6e73', marginBottom: '12px' }}>선택지 (Options)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {options.map((option, index) => {
                        const label = getOptionLabel(index); // "A", "B", ...
                        const displayValue = cleanOptionText(option, index);
                        const isCorrect = option === correctAnswer;
                        
                        return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {/* Checkmark Circle */}
                                <div 
                                    onClick={() => handleCorrectAnswerSelect(option)}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        border: isCorrect ? 'none' : '2px solid #D2D2D7',
                                        backgroundColor: isCorrect ? '#34C759' : 'transparent',
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {isCorrect && (
                                        <Icon 
                                            name="check" 
                                            style={{ 
                                                color: 'white', 
                                                margin: 0, 
                                                padding: 0,
                                                fontSize: '12px', 
                                                height: 'auto',
                                                width: 'auto'
                                            }} 
                                        />
                                    )}
                                </div>

                                {/* Label (A.) */}
                                <div style={{ 
                                    flex: '0 0 auto', 
                                    fontWeight: '600', 
                                    color: '#1d1d1f', 
                                    fontSize: '15px',
                                    width: '24px',
                                    textAlign: 'center'
                                }}>
                                    {label}.
                                </div>

                                {/* Input Field */}
                                <div style={{ flex: 1 }}>
                                    <Input 
                                        fluid 
                                        placeholder={`Enter option text...`}
                                        value={displayValue}
                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                        input={{ style: inputStyle }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p style={{ fontSize: '12px', color: '#86868b', marginTop: '12px', marginLeft: '36px' }}>
                    * 녹색 체크박스를 클릭하여 정답을 선택하세요.
                </p>
            </Form.Field>

            <Form.Field>
                <label style={{ fontSize: '13px', color: '#1d1d1f', fontWeight: '500', marginBottom: '8px' }}>해설 (Explanation)</label>
                <TextArea
                    placeholder="정답에 대한 해설을 입력하세요..."
                    value={explanation}
                    onChange={handleExplanationChange}
                    rows={3}
                    style={{ ...inputStyle, resize: 'none', lineHeight: '1.5' }}
                />
            </Form.Field>
        </div>
    );
};

const OpenEndedEditor = ({ meta, onChange }) => {
    const safeMeta = meta || {};

    return (
        <div style={{ marginTop: '30px', padding: '24px', backgroundColor: '#F5F5F7', borderRadius: '16px' }}>
            <Header as="h4" style={{ color: '#1d1d1f', marginBottom: '20px' }}>주관식 설정 (Open Ended)</Header>

            <Form.Field>
                <label style={{ fontSize: '13px', color: '#1d1d1f', fontWeight: '500', marginBottom: '8px' }}>모범 답안 (Model Answer)</label>
                <TextArea
                    placeholder="정답으로 인정할 모범 답안을 입력하세요..."
                    value={safeMeta.correct_answer || ''}
                    onChange={(e, { value }) => onChange({ ...safeMeta, correct_answer: value })}
                    rows={3}
                    style={{ ...inputStyle, resize: 'none', lineHeight: '1.5' }}
                />
            </Form.Field>

            <Form.Field>
                <label style={{ fontSize: '13px', color: '#1d1d1f', fontWeight: '500', marginBottom: '8px' }}>채점 기준 (Grading Rubric)</label>
                <TextArea
                    placeholder="답안을 채점할 기준을 입력하세요..."
                    value={safeMeta.rubric || ''}
                    onChange={(e, { value }) => onChange({ ...safeMeta, rubric: value })}
                    rows={4}
                    style={{ ...inputStyle, resize: 'none', lineHeight: '1.5' }}
                />
                <p style={{ fontSize: '12px', color: '#86868b', marginTop: '8px' }}>
                    * 채점 기준을 입력하면 AI가 이를 기준으로 답안을 평가합니다. 비워두면 모범 답안과 비교합니다.
                </p>
            </Form.Field>
        </div>
    );
};

const QuizQuestionDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { data: question, isLoading, error } = useGetQuizQuestionQuery(id);
    const { data: productsData } = useGetProductsQuery({ page_size: 1000 });
    const [updateQuestion, { isLoading: isUpdating }] = useUpdateQuizQuestionMutation();

    const [formData, setFormData] = useState({
        product: '',
        source_evaluation: '',
        question_text: '',
        question_type: 'MC',
        answer_meta: {},
        citation_source: '',
        citation_url: '',
        is_active: true
    });

    const [isDirty, setIsDirty] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    useEffect(() => {
        if (question) {
            setFormData({
                product: question.product || '',
                source_evaluation: question.source_evaluation || '',
                question_text: question.question_text || '',
                question_type: question.question_type || 'MC',
                answer_meta: question.answer_meta || {},
                citation_source: question.citation_source || '',
                citation_url: question.citation_url || '',
                is_active: question.is_active ?? true
            });
        }
    }, [question]);

    const handleChange = (e, { name, value, checked }) => {
        const val = name === 'is_active' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: val }));
        setIsDirty(true);
        setSaveSuccess(false);
    };

    const handleMetaChange = (newMeta) => {
        setFormData(prev => ({ ...prev, answer_meta: newMeta }));
        setIsDirty(true);
        setSaveSuccess(false);
    }

    const handleSave = async () => {
        try {
            const payload = {
                id,
                ...formData,
                answer_meta: formData.answer_meta 
            };

            await updateQuestion(payload).unwrap();
            setIsDirty(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error('Failed to update question:', err);
        }
    };

    const productOptions = productsData?.results?.map(p => ({
        key: p.id,
        value: p.id,
        text: p.name
    })) || [];

    const panes = [
        {
            menuItem: '기본 정보',
            render: () => (
                <Tab.Pane style={innerTabPaneStyle}>
                    <Form loading={isLoading || isUpdating} success={saveSuccess}>
                        {saveSuccess && (
                            <Message 
                                success 
                                header="저장됨" 
                                content="퀴즈 문제가 성공적으로 업데이트되었습니다."
                                style={{ borderRadius: '12px', marginBottom: '24px' }}
                            />
                        )}

                        <Header as="h4" dividing style={{ color: '#1d1d1f', marginBottom: '20px' }}>문제 설정</Header>
                        
                        <Form.Group widths="equal">
                             <Form.Field>
                                <label style={{ marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' }}>관련 제품</label>
                                <Select
                                    options={productOptions}
                                    placeholder="제품 선택"
                                    name="product"
                                    value={formData.product}
                                    onChange={handleChange}
                                    search
                                    fluid
                                    style={{ backgroundColor: '#F5F5F7', border: 'none', borderRadius: '10px' }}
                                />
                             </Form.Field>
                             <Form.Field>
                                <label style={{ marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' }}>문제 유형</label>
                                <Select
                                    name="question_type"
                                    options={QUESTION_TYPES}
                                    value={formData.question_type}
                                    onChange={handleChange}
                                    fluid
                                    style={{ backgroundColor: '#F5F5F7', border: 'none', borderRadius: '10px' }}
                                />
                             </Form.Field>
                        </Form.Group>

                         <Form.Group>
                            {/* Chat Session Link - Takes more space (12/16) */}
                            <Form.Field width={12}>
                                <label style={{ marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' }}>채팅 세션 (Source Session)</label>
                                {question?.chat_session_id ? (
                                    <div style={{ 
                                        padding: '12px 16px', 
                                        backgroundColor: '#F5F5F7', 
                                        borderRadius: '10px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        height: '48px' // Enforce consistent height
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', height: '100%' }}>
                                            <Icon name="chat" style={{ color: '#007AFF', margin: 0, padding: 0, display: 'flex', alignItems: 'center' }} />
                                            <span style={{ fontWeight: '500', color: '#1d1d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingTop: '1px' }}>
                                                {question.chat_session_str || `Session ${question.chat_session_id}`}
                                            </span>
                                        </div>
                                        <Button 
                                            size="tiny" 
                                            basic 
                                            primary 
                                            onClick={() => window.open(`/chatsessions/${question.chat_session_id}`, '_blank')}
                                            style={{ boxShadow: 'none', marginLeft: '10px', flexShrink: 0, background: 'transparent' }}
                                        >
                                            세션 보기
                                            <Icon name="external" style={{ marginLeft: '6px' }} />
                                        </Button>
                                    </div>
                                ) : (
                                    <div style={{ 
                                        padding: '12px 16px', 
                                        backgroundColor: '#F5F5F7', 
                                        borderRadius: '10px', 
                                        color: '#86868b',
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        연결된 채팅 세션 없음
                                    </div>
                                )}
                            </Form.Field>

                            {/* Active Toggle - Takes less space (4/16) */}
                            <Form.Field width={4}>
                                <label style={{ marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' }}>상태 (Status)</label>
                                <div style={{ 
                                    padding: '0 16px', 
                                    backgroundColor: '#F5F5F7', 
                                    borderRadius: '10px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    height: '48px' // Match height of Chat Session box
                                }}>
                                    <span style={{ fontSize: '14px', color: formData.is_active ? '#1d1d1f' : '#86868b', fontWeight: '500' }}>
                                        {formData.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <Form.Checkbox
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        toggle
                                        style={{ margin: 0, padding: 0 }}
                                    />
                                </div>
                            </Form.Field>
                        </Form.Group>

                        <Form.Field required>
                            <label style={{ marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' }}>질문 내용</label>
                            <TextArea
                                name="question_text"
                                value={formData.question_text}
                                onChange={handleChange}
                                rows={3}
                                style={{ 
                                    resize: 'none', 
                                    padding: '16px', 
                                    borderRadius: '12px', 
                                    backgroundColor: '#F5F5F7', 
                                    border: 'none',
                                    fontSize: '15px'
                                }}
                            />
                        </Form.Field>
                        
                        {formData.question_type === 'MC' ? (
                            <MultipleChoiceEditor 
                                meta={typeof formData.answer_meta === 'string' ? {} : formData.answer_meta} 
                                onChange={handleMetaChange} 
                            />
                        ) : (
                            <OpenEndedEditor
                                meta={typeof formData.answer_meta === 'string' ? {} : formData.answer_meta}
                                onChange={handleMetaChange}
                            />
                        )}
                        
                        <div style={{ marginTop: '40px' }} />
                        
                        <Header as="h4" dividing style={{ color: '#1d1d1f', marginTop: '30px', marginBottom: '20px' }}>출처 및 인용</Header>
                        <div style={{ padding: '16px', backgroundColor: '#F5F5F7', borderRadius: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#1d1d1f', marginBottom: '8px' }}>참고 링크</label>
                            {formData.citation_url ? (
                                <a 
                                    href={formData.citation_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#007AFF', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
                                >
                                    {formData.citation_source || formData.citation_url}
                                    <Icon name="external alternate" style={{ fontSize: '12px', margin: 0, verticalAlign: 'middle', lineHeight: 1 }} />
                                </a>
                            ) : (
                                <span style={{ color: '#86868b' }}>
                                    {formData.citation_source || '링크 없음'}
                                </span>
                            )}
                        </div>

                        <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                                primary 
                                onClick={handleSave} 
                                disabled={!isDirty} 
                                loading={isUpdating}
                                style={{ borderRadius: '10px', height: '40px', padding: '0 24px' }}
                            >
                                변경사항 저장
                            </Button>
                        </div>
                    </Form>
                </Tab.Pane>
            )
        }
    ];

    if (isLoading) return <Loader active />;
    if (error) {
        return (
            <Container style={{ padding: '40px' }}>
                <Message error>
                    <Message.Header>오류</Message.Header>
                    <p>퀴즈 정보를 불러오는데 실패했습니다.</p>
                </Message>
                <Button onClick={() => navigate(-1)}>뒤로 가기</Button>
            </Container>
        );
    }

    return (
        <div style={{ height: 'auto', display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#FBFBFD' }}>
             {/* Header Section */}
            <div style={{
                flex: '0 0 auto',
                padding: '16px 0',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <Container fluid>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: isMobile ? '0 20px' : '0 40px' }}>
                        <Button 
                            icon 
                            basic 
                            onClick={() => navigate('/admin/quiz-questions')}
                            style={{ boxShadow: 'none', background: 'transparent', padding: '10px', margin: 0 }}
                        >
                            <Icon name="arrow left" style={{ color: '#007AFF', fontSize: '18px', margin: 0 }} />
                        </Button>
                        
                        <div style={{ width: '40px', flexShrink: 0, height: '40px', borderRadius: '50%', backgroundColor: '#F2F2F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="question circle outline" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>
                                퀴즈 상세 정보
                            </h2>
                            <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                                #{question.id} • {question.question_type}
                            </p>
                        </div>
                    </div>
                </Container>
            </div>

            <Container fluid style={{ padding: isMobile ? '20px' : '40px', flex: 1, maxWidth: '1000px' }}>
                 <Tab 
                    menu={{ secondary: true, pointing: true, style: { marginBottom: '20px' } }} 
                    panes={panes} 
                />
            </Container>
        </div>
    );
};

export default QuizQuestionDetails;
